/**
 * A regra de quando a etapa pede dinheiro — e de quando ela JÁ recebeu.
 *
 * Existe porque três status do catálogo têm `exigePagamento: true` (`pago`,
 * `pago_parcial` e `retirado`), e o caminho normal do balcão passa por dois
 * deles em sequência: recebe e marca "Pago", depois o cliente busca o
 * aparelho e a OS vira "Retirado".
 *
 * Tratar `exigePagamento` como "digite um valor agora" fazia a SEGUNDA etapa
 * cobrar de novo um dinheiro que já entrou: o campo era obrigatório, o
 * operador redigitava o valor da OS e `total_pago` ficava o dobro do orçado —
 * inflando junto o extrato do caixa e o "Faturamento recebido" do dashboard,
 * porque `rpc_atualizar_status` grava nas duas tabelas.
 *
 * O que a flag quer dizer, de verdade, é "esta etapa não pode existir com a
 * OS devendo". Se a OS já recebeu alguma coisa, o campo continua na tela para
 * lançar um COMPLEMENTO, mas vazio ele não lança nada.
 *
 * As duas telas que movem etapa (`StatusOs` e `ModalOs`) usam este mesmo
 * cálculo de propósito: a divergência entre elas é o tipo de coisa que volta
 * como valor duplicado em produção.
 */
import type { ResumoFinanceiroOs } from '@tcm/shared';

export interface RegrasDePagamento {
  /** O valor precisa ser preenchido para a etapa poder ser gravada. */
  obrigatorio: boolean;
  /** Quanto a OS ainda deve. Zero ou negativo quando quitada. */
  faltaPagar: number;
  /** Quanto já entrou nesta OS, somando todos os lançamentos. */
  jaRecebido: number;
  /**
   * Quanto o valor digitado passaria do orçado, se passar.
   *
   * Zero quando a OS não tem orçamento lançado: `valor_orcado = 0` é o estado
   * de 100+ OS da base, e ali qualquer pagamento "excede" o orçado sem que
   * isso signifique erro nenhum.
   */
  excedente: number;
}

export function regrasDePagamento(
  financeiro: ResumoFinanceiroOs,
  exigePagamento: boolean,
  valorCentavos: number,
): RegrasDePagamento {
  const jaRecebido = financeiro.totalPagoCentavos;
  const orcado = financeiro.valorOrcadoCentavos;

  return {
    obrigatorio: exigePagamento && jaRecebido <= 0,
    faltaPagar: financeiro.saldoDevedorCentavos,
    jaRecebido,
    excedente: orcado > 0 ? Math.max(0, jaRecebido + valorCentavos - orcado) : 0,
  };
}
